import { type CSSResult, html, LitElement, nothing, unsafeCSS } from 'lit';
import { customElement, property, query, state } from 'lit/decorators.js';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import { unsafeSVG } from 'lit/directives/unsafe-svg.js';
import { idleTick } from '../../base/package/util/render';
import { addSVG, getSVG, hasSVG, loadingSVG } from './svg-cache';
import styles from './ui-inlined-svg.css?inline';

const CName = 'ui-inlined-svg';

/**
 * @prop {CSSResult} css - CSS styles to apply directly to the SVG.
 * @prop {boolean} defer - Load SVG when the browser is idle.
 * @prop {boolean} fade - Fade in the SVG once loaded.
 * @prop {string} src - Source URL for the SVG to inline.
 * @prop {string} label - Accessible text label for the SVG.
 */
@customElement(CName)
export class UIInlinedSvg extends LitElement {
  static readonly styles = unsafeCSS(styles);

  @property({ type: Object }) css?: CSSResult;
  @property({ type: Boolean }) defer = false; // Load when browser is idle
  @property({ type: Boolean }) fade = false; // Fade in when loaded
  @property({ type: String }) src?: string;
  @property({ type: String }) label?: string;

  @state() private _svgContent?: string;

  @query('svg') private readonly _svgElement?: SVGSVGElement;

  async connectedCallback() {
    super.connectedCallback();
    if (this.defer) await idleTick();
    await this._loadSVG();
  }

  updated(changed: Map<string, unknown>) {
    if (changed.has('src') || changed.has('label')) {
      this._loadSVG();
    }
    if (this._svgElement && this.label) {
      this._svgElement.role = 'img';
      this._svgElement.setAttribute('aria-label', this.label);
      if (!this._svgElement.querySelector('title')) {
        const title = document.createElementNS('http://www.w3.org/2000/svg', 'title');
        title.textContent = this.label;
        this._svgElement.insertBefore(title, this._svgElement.firstChild);
      }
    }
  }

  render() {
    if (!this._svgContent) return nothing;
    return html`
      ${unsafeHTML(this._inlineCss())}${unsafeSVG(this._svgContent)}
    `;
  }

  private _inlineCss() {
    if (!this.css) return '';
    const fadeCSS = this.fade
      ? '<style>svg { animation: fadeIn 0.3s forwards; } @keyframes fadeIn { from { opacity: 0; } }</style>'
      : '';
    return `${fadeCSS}<style>${this.css.toString()}</style>`;
  }

  private async _loadSVG(): Promise<void> {
    if (this.src === undefined) {
      return;
    }
    if (await hasSVG(this.src)) {
      this._svgContent = getSVG(this.src);
      return;
    }
    loadingSVG(this.src);
    try {
      const res = await fetch(this.src);
      const raw = await res.text();
      this._svgContent = raw;
      addSVG(this.src, raw);
    } catch (e) {
      window.$app.logger.warn('SVG load error:', this.src, e);
    }
  }
}

declare global {
  interface HTMLElementTagNameMap {
    [CName]: UIInlinedSvg;
  }
}
